import { Typography, Divider, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function PrivacyPolicy() {
    const navigate = useNavigate();

    return (
        <div className="privacy-policy-page">
            <Box sx={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
                <Typography sx={{ textAlign: "center", color: "white", flex: 1 }} variant="h4">
                    Tamil Football – Privacy Policy
                </Typography>
            </Box>

            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body2">
                <strong>Last Updated:</strong> November 5, 2025
            </Typography>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                Welcome to Tamil Football. We respect your privacy and are committed to protecting your personal information.
                This Privacy Policy explains how we collect, use, and safeguard data when you use our app. Our goal is to
                keep your experience secure, transparent, and reliable.
            </Typography>

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                1. Collection of Personal Information
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                Tamil Football collects personal information in two main ways:
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "10px", fontWeight: 600 }} variant="body1">
                a) Account Registration
            </Typography>
            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                When you create a user account, we collect:
            </Typography>
            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>First name and last name</li>
                <li>Email address</li>
                <li>Password (securely encrypted)</li>
                <li>Date of birth</li>
                <li>Jersey number (optional)</li>
                <li>Club affiliation (optional)</li>
                <li>Profile picture (optional)</li>
            </ul>
            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                This information is used to create and manage your Tamil Football profile, verify eligibility for competitions,
                and personalize your experience within the app.
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "10px", fontWeight: 600 }} variant="body1">
                b) Team Management and Player Data
            </Typography>
            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                Team leaders can manage team rosters for tournaments by adding player information. They can either:
            </Typography>
            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>Enter player details manually (name, date of birth, jersey number), or</li>
                <li>Fetch information from an existing user profile within Tamil Football to simplify roster creation.</li>
            </ul>
            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                Fetching profile information helps pre-fill details, but each tournament still requires its own team sheet
                submission to maintain accurate competition records.
            </Typography>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                2. Use of Personal Information
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                We use collected data to:
            </Typography>
            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>Create and manage user accounts and player profiles</li>
                <li>Organize and manage teams, rosters, and tournaments</li>
                <li>Verify player eligibility (for example, age-based competitions)</li>
                <li>Display player and team information within the app</li>
                <li>Streamline team creation by allowing data to be fetched from registered profiles</li>
            </ul>
            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                Tamil Football does not use your personal data for advertising or analytics purposes.
            </Typography>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                3. Linked Profiles and Data Visibility
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                When a team leader fetches player information from a registered user's profile:
            </Typography>
            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>Only the following details are imported: name, date of birth, email address, jersey number (if provided),
                    and club affiliation (if provided).</li>
                <li>The email address is used solely to connect or invite the user to the tournament team.</li>
                <li>The fetched data is stored as part of that tournament's team sheet and does not automatically update if
                    the user later changes their profile.</li>
                <li>Team leaders can view this data only within their own teams and tournaments.</li>
                <li>Fetching profile information does not provide access to private data such as passwords or unrelated
                    personal settings.</li>
                <li>Players (or parents/guardians, if underage) can request correction or removal of their data from a team
                    sheet at any time by contacting Tamil Football or their team leader.</li>
            </ul>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                4. Usage Data and Device Information
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                Tamil Football does not collect or store detailed usage data or device information. All activity in the app
                is anonymous except as needed for account, team, and tournament management.
            </Typography>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                5. Sharing of Information
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                We do not sell, rent, or share personal information with third parties. Information is used only within
                Tamil Football for legitimate sports-management purposes such as team rosters, tournament organization, and
                eligibility checks. We do not use external advertising or analytics services.
            </Typography>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                6. Data Security
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                We use strong security measures to protect your information, including:
            </Typography>
            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>Encrypted password storage</li>
                <li>Secure HTTPS connections</li>
                <li>Role-based access controls</li>
            </ul>
            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                Users should also:
            </Typography>
            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>Keep login credentials private</li>
                <li>Use strong passwords</li>
                <li>Keep devices and apps updated</li>
            </ul>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                7. Data Retention and Deletion
            </Typography>

            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>User profile information is stored as long as your account remains active.</li>
                <li>Team and tournament data are retained for historical and competition purposes.</li>
                <li>Team leaders can modify or remove team information until tournament deadlines.</li>
                <li>After deadlines, data may be locked for competition integrity but can still be corrected or deleted
                    upon request.</li>
                <li>Unless a deletion request is made, certain data (e.g., historical tournament records) may be retained
                    indefinitely.</li>
            </ul>
            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                Users can delete their profile or request deletion of associated data at any time.
            </Typography>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                8. Legal Basis and User Rights
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                Tamil Football processes personal data only for legitimate purposes, such as:
            </Typography>
            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>Managing user accounts</li>
                <li>Organizing and verifying team and tournament participation</li>
                <li>Ensuring compliance with competition rules</li>
            </ul>
            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                Users (and parents/guardians, if underage) have the right to:
            </Typography>
            <ul style={{ color: "white", marginBottom: "20px" }}>
                <li>Access their personal data</li>
                <li>Request correction or deletion</li>
                <li>Withdraw consent for processing (by deleting their account)</li>
                <li>Request removal from a team or tournament</li>
                <li>Ask how their data is used and stored</li>
            </ul>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                9. Changes to This Privacy Policy
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "20px" }} variant="body1">
                We may update this Privacy Policy periodically to reflect new features or legal requirements. Updates take
                effect when published in the app, with the "Last Updated" date shown above.
            </Typography>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", marginY: "20px" }} />

            <Typography sx={{ color: "white", marginTop: "30px", marginBottom: "15px" }} variant="h5">
                10. Contact Us
            </Typography>

            <Typography sx={{ color: "white", marginBottom: "10px" }} variant="body1">
                If you have questions or requests about this Privacy Policy or your data, please contact us at:
            </Typography>
            <ul style={{ color: "white", marginBottom: "40px" }}>
                <li>📧 Email: jebisan1@gmail.com (subject to change)</li>
                <li>📞 Phone: +45 28 60 66 68 (subject to change)</li>
            </ul>
        </div>
    );
}

export default PrivacyPolicy;
